import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, ProgressBar, Card, useTheme } from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { AssessmentProgress } from './types';

interface AssessmentProgressProps {
  progress: AssessmentProgress;
  showDetails?: boolean;
  style?: any;
}

export function AssessmentProgress({
  progress,
  showDetails = true,
  style,
}: AssessmentProgressProps) {
  const theme = useTheme();

  const completionPercentage = progress.currentStep / progress.totalSteps;
  const questionsPercentage = progress.completedQuestions / progress.totalSteps;

  const formatTime = (minutes: number): string => {
    if (minutes < 1) return 'Less than 1 min';
    if (minutes === 1) return '1 min';
    return `${Math.round(minutes)} mins`;
  };

  return (
    <Card style={[styles.container, style]} mode='outlined'>
      <Card.Content>
        {/* Main Progress */}
        <View style={styles.mainProgress}>
          <View style={styles.progressHeader}>
            <Text variant='titleMedium' style={styles.progressTitle}>
              Assessment Progress
            </Text>
            <Text
              variant='bodyMedium'
              style={[styles.stepCounter, { color: theme.colors.primary }]}
            >
              {progress.currentStep} of {progress.totalSteps}
            </Text>
          </View>

          <ProgressBar
            progress={completionPercentage}
            color={theme.colors.primary}
            style={styles.mainProgressBar}
          />

          <Text
            variant='bodySmall'
            style={[
              styles.progressText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            {Math.round(completionPercentage * 100)}% complete
          </Text>
        </View>

        {showDetails && (
          <>
            {/* Confidence Level */}
            <View style={styles.confidenceSection}>
              <Text variant='bodyMedium' style={styles.sectionTitle}>
                Response Confidence
              </Text>
              <ConfidenceIndicator
                confidence={progress.averageConfidence}
                showLabel={true}
                showDescription={false}
                size='small'
              />
            </View>

            {/* Statistics */}
            <View style={styles.statsSection}>
              <View style={styles.statsRow}>
                <View style={styles.statItem}>
                  <Text
                    variant='bodyLarge'
                    style={[styles.statValue, { color: theme.colors.primary }]}
                  >
                    {progress.completedQuestions}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.statLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Completed
                  </Text>
                </View>

                <View style={styles.statItem}>
                  <Text
                    variant='bodyLarge'
                    style={[
                      styles.statValue,
                      { color: theme.colors.secondary },
                    ]}
                  >
                    {progress.skippedQuestions}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.statLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Skipped
                  </Text>
                </View>

                <View style={styles.statItem}>
                  <Text
                    variant='bodyLarge'
                    style={[styles.statValue, { color: theme.colors.tertiary }]}
                  >
                    {formatTime(progress.estimatedTimeRemaining)}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.statLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Remaining
                  </Text>
                </View>
              </View>
            </View>

            {/* Category Progress */}
            {Object.keys(progress.categoryProgress).length > 0 && (
              <View style={styles.categorySection}>
                <Text variant='bodyMedium' style={styles.sectionTitle}>
                  Category Progress
                </Text>
                {Object.entries(progress.categoryProgress).map(
                  ([category, categoryProgress]) => (
                    <View key={category} style={styles.categoryItem}>
                      <View style={styles.categoryHeader}>
                        <Text
                          variant='bodySmall'
                          style={[
                            styles.categoryName,
                            { color: theme.colors.onSurface },
                          ]}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </Text>
                        <Text
                          variant='bodySmall'
                          style={[
                            styles.categoryPercentage,
                            { color: theme.colors.onSurfaceVariant },
                          ]}
                        >
                          {Math.round(categoryProgress * 100)}%
                        </Text>
                      </View>
                      <ProgressBar
                        progress={categoryProgress}
                        color={theme.colors.tertiary}
                        style={styles.categoryProgressBar}
                      />
                    </View>
                  )
                )}
              </View>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  mainProgress: {
    marginBottom: 16,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressTitle: {
    fontWeight: '600',
  },
  stepCounter: {
    fontWeight: '600',
  },
  mainProgressBar: {
    height: 8,
    borderRadius: 4,
    marginBottom: 4,
  },
  progressText: {
    textAlign: 'center',
  },
  confidenceSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  statsSection: {
    marginBottom: 16,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontWeight: '700',
    fontSize: 18,
  },
  statLabel: {
    marginTop: 2,
  },
  categorySection: {
    marginTop: 8,
  },
  categoryItem: {
    marginBottom: 12,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  categoryName: {
    fontWeight: '500',
  },
  categoryPercentage: {
    fontSize: 12,
  },
  categoryProgressBar: {
    height: 4,
    borderRadius: 2,
  },
});
